<script>
const ICON_POSITION = Object.freeze({
  TOP_RIGHT: "top-right",
  BOTTOM_RIGHT: "bottom-right",
});
const AVATAR_SIZE = Object.freeze({
  BASE: "base",
  SMALL: "small",
  MEDIUM: "medium",
});
export const AVATAR_THEME = Object.freeze({
  LIGHT: "light",
  DARK: "dark",
  TRANSPARENT: "transparent",
  NEUTRAL: "neutral",
  BLUE: "blue",
  WHITE: "white",
});
</script>

<script setup>
defineProps({
  tooltip: {
    type: String,
    default: "",
  },
  tooltipSemibold: {
    type: Boolean,
    default: false,
  },
  shortName: {
    type: String,
    default: null,
  },
  showNAinFallBack: {
    type: Boolean,
    default: true,
  },
  iconVisible: {
    type: Boolean,
    default: true,
  },
  theme: {
    type: String,
    validator: (prop) => Object.values(AVATAR_THEME).includes(prop),
    default: AVATAR_THEME.DARK,
  },
  iconPosition: {
    type: String,
    validator: (prop) => Object.values(ICON_POSITION).includes(prop),
    default: ICON_POSITION.BOTTOM_RIGHT,
  },
  iconHalfRounded: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    validator: (prop) => Object.values(AVATAR_SIZE).includes(prop),
    default: AVATAR_SIZE.SMALL,
  },
});
</script>

<template>
  <div
    class="avatar-wrapper position-relative rounded-circle"
    :class="{ [size]: size }"
  >
    <span
      class="avatar fw-bold d-flex justify-content-center align-items-center rounded-circle"
      :class="{ [theme]: theme, 'tooltip-semibold': tooltipSemibold }"
      v-b-tooltip.hover.top="tooltip"
    >
      <slot>
        <span v-if="shortName">{{ shortName }}</span>
        <span v-if="!shortName && showNAinFallBack">N/A</span>
      </slot>
    </span>
    <span
      v-if="$slots['icon'] && iconVisible"
      class="icon-marker position-absolute"
      :class="{ [iconPosition]: iconPosition, iconHalfRounded }"
    >
      <slot name="icon" />
    </span>
  </div>
</template>

<style scoped lang="scss">
.avatar-wrapper {
  &.small {
    .avatar {
      width: 2rem;
      height: 2rem;
      font-size: 12px;
    }

    .icon-marker {
      width: 1rem;
      height: 1rem;

      &.bottom-right {
        top: 14px;
        left: 20px;
      }

      &.top-right {
        bottom: 16px;
        left: 21px;
      }
    }
  }

  &.medium {
    .avatar {
      width: 2.5rem;
      height: 2.5rem;
      font-size: 14px;
    }

    .icon-marker {
      width: 1.5rem;
      height: 1.5rem;

      &.bottom-right {
        top: 20px;
        left: 28px;
      }

      &.top-right {
        bottom: 20px;
        left: 28px;
      }
    }
  }

  &.base {
    .avatar {
      width: 2rem;
      height: 2rem;
      font-size: 14px;
    }
  }

  .avatar {
    &.dark {
      color: $bluish-gray-50;
      background: $bluish-gray-800;
      border: none;
    }

    &.light {
      color: var(--primary-400);
      background: var(--primary-50);
      border: none;
    }

    &.transparent {
      color: $neutral-300;
      border: 1px solid $neutral-200;
    }

    &.white {
      color: $neutral-300;
      border: 1px solid $neutral-200;
      background: #fff;
    }

    &.white {
      color: $neutral-300;
      background: $neutral-0;
      border: 1px solid $neutral-200;
    }

    &.neutral {
      color: $neutral-350;
      background: $neutral-150;
      border: 1px solid $neutral-200;
    }

    &.blue {
      color: $neutral-0;
      background: var(--primary-250);
      border: 1px solid $neutral-200;
    }
  }

  .icon-marker {
    border-radius: 6px;

    span {
      border-radius: 6px;
    }

    &.iconHalfRounded {
      border-radius: 50%;

      span {
        border-radius: 50%;
      }
    }
  }
}
</style>
